/**
 * selection-scope.js — what row 2 is currently about.
 *
 * Two models, and keeping them apart is the whole design (contracts §1):
 *
 *   ScopeIdentity  — WHICH pane is mounted. Derived from settled stores only.
 *                    Changes only when the selection changes.
 *   PaneViewModel  — WHAT the mounted pane displays. Labels, lock state,
 *                    writability, capabilities, the active viewport. Changes on
 *                    document edits too.
 *
 * Collapsing them would make every override write re-mount a pane, and the morph
 * is a 190 ms user-visible animation. Splitting them means an undo that changes
 * a value repaints the value and nothing else.
 *
 * ── Derived from stores, NEVER from the raw click stream ──────────────────
 * `iframe:element-clicked` can be CONSUMED by AI pick mode
 * (`selection-controller.js` returns on it, and so does
 * `active-editable-controller.js`). A listener on the raw event would show a
 * text pane for a click that deliberately selected nothing. So the inputs here
 * are the two settled stores and nothing else.
 *
 * ── The standing `state-changed` rule, and its one exception ──────────────
 * "Do not invalidate a cache on `state-changed`" (16C2) exists because that
 * event fires on every `update()`, including inside a transaction, so a cache
 * keyed on it never survives a scrub. That rule holds for ScopeIdentity, which
 * is why identity is derived from store subscriptions.
 *
 * PaneViewModel is the exception and it is deliberate: it is a handful of
 * strings and booleans, it legitimately changes through undo, responsive edits
 * and collab, and it is recomputed — not cached — behind the same rAF coalescer.
 * A scrub therefore produces at most one recompute per frame, which is the cost
 * the rule was protecting against in the first place.
 */

import { eventBus } from '../../core/events.js';
import { store } from '../../core/state.js';
import { selection } from '../../core/selection-store.js';
import { onActiveEditableChange, readActiveEditable } from '../../editing/active-editable-store.js';
import { sharedTypeOf } from '../../shared/section-order.js';
import { formatSectionName } from '../../shared/section-display-name.js';
import { sectionCapabilities } from './section-command-service.js';
import { capabilitiesForTarget } from './editable-capabilities.js';
import { hasAlt as imageHasAlt } from './image-command-service.js';
import { getActiveViewportName } from '../viewport-controls.js';
import { kindLabel } from '../../shared/element-kind.js';
import { targetRefKey } from '../../shared/target-ref.js';

/** The six panes row 2 can mount. */
export const SCOPE = Object.freeze({
  PAGE: 'page',
  SECTION: 'section',
  MULTI: 'multi',
  TEXT: 'text',
  IMAGE: 'image',
  ELEMENT: 'element'
});

/** Editable types that get their own pane. Everything else is `element`. */
const TYPED_PANES = Object.freeze({ text: SCOPE.TEXT, image: SCOPE.IMAGE });

/**
 * Every instance id that can legally own an editable RIGHT NOW: the active
 * page's sections plus the shared nav/footer.
 *
 * An editable whose section is not in this set is STALE — it survived a page
 * switch or a delete that the store has not yet been told about. Rule 1 of the
 * precedence falls through in that case rather than naming a section the user
 * cannot see.
 */
function liveInstanceIds(state) {
  const ids = new Set();
  const shared = state?.sharedSections;
  if (shared?.navigation?.instanceId) ids.add(shared.navigation.instanceId);
  if (shared?.footer?.instanceId) ids.add(shared.footer.instanceId);
  const page = (state?.pages || []).find((p) => p?.id === state?.activePage);
  for (const s of page?.sections || []) if (s?.instanceId) ids.add(s.instanceId);
  return ids;
}

function findSection(state, instanceId) {
  const sharedType = sharedTypeOf(state, instanceId);
  if (sharedType) return state.sharedSections[sharedType];
  const page = (state?.pages || []).find((p) => p?.id === state?.activePage);
  return (page?.sections || []).find((s) => s?.instanceId === instanceId) || null;
}

/**
 * Derive the scope identity. Pure: takes a state, returns a value.
 *
 * Precedence (contracts §1), in this order and no other:
 *   1. a live active editable          → text | image | element
 *   2. selection.size() > 1            → multi
 *   3. selection.size() === 1          → section   (shared nav/footer INCLUDED)
 *   4. otherwise                       → page
 *
 * @returns {{kind: string, instanceId: string|null, eid: string|null,
 *            editableType: string|null, count: number}}
 */
export function deriveScopeIdentity(state = store.getState()) {
  const editable = readActiveEditable();
  if (
    editable?.instanceId &&
    editable?.targetRef &&
    liveInstanceIds(state).has(editable.instanceId)
  ) {
    return {
      kind: TYPED_PANES[editable.editableType] || SCOPE.ELEMENT,
      instanceId: editable.instanceId,
      eid: editable.eid,
      targetRef: editable.targetRef,
      targetKey: targetRefKey(editable.targetRef),
      editableType: editable.editableType || null,
      elementKind: editable.kind || 'unknown',
      count: 1
    };
  }

  const size = selection.size();
  if (size > 1) {
    return {
      kind: SCOPE.MULTI,
      instanceId: selection.getPrimary(),
      eid: null,
      targetRef: null,
      targetKey: '',
      editableType: null,
      elementKind: null,
      count: size
    };
  }
  if (size === 1) {
    return {
      kind: SCOPE.SECTION,
      instanceId: selection.getPrimary(),
      eid: null,
      targetRef: null,
      targetKey: '',
      editableType: null,
      elementKind: null,
      count: 1
    };
  }
  return {
    kind: SCOPE.PAGE,
    instanceId: null,
    eid: null,
    targetRef: null,
    targetKey: '',
    editableType: null,
    elementKind: null,
    count: 0
  };
}

/** Identity equality — the dedupe key. Five scalars; no deep walk needed. */
function sameIdentity(a, b) {
  if (!a || !b) return a === b;
  return (
    a.kind === b.kind &&
    a.instanceId === b.instanceId &&
    a.targetKey === b.targetKey &&
    a.editableType === b.editableType &&
    a.elementKind === b.elementKind &&
    a.count === b.count
  );
}

/**
 * Everything the mounted pane renders, for the given identity.
 *
 * Recomputed rather than cached — see the header. Returns a fresh object every
 * time, so a consumer holding the previous one can diff against it.
 *
 * @param {object} identity
 * @param {object} [state]
 * @returns {object}
 */
export function derivePaneViewModel(identity, state = store.getState()) {
  const activePayload = readActiveEditable();
  const payloadMatchesIdentity = Boolean(
    activePayload &&
    identity.instanceId === activePayload.instanceId &&
    identity.targetKey === targetRefKey(activePayload.targetRef) &&
    identity.editableType === activePayload.editableType
  );
  const base = {
    kind: identity.kind,
    instanceId: identity.instanceId,
    eid: identity.eid,
    targetRef: identity.targetRef,
    targetKey: identity.targetKey,
    editableType: identity.editableType,
    elementKind: identity.elementKind,
    count: identity.count,
    writable: typeof store.isWritable === 'function' ? store.isWritable() : true,
    viewport: getActiveViewportName(),
    label: '',
    sublabel: '',
    sharedType: null,
    locked: false,
    capabilities: null,
    // ScopeIdentity deliberately remains five cheap scalars. The mounted pane,
    // however, must retain the authoritative type-specific snapshot: image
    // replacement needs editableIndex, glass panels need computed metadata,
    // and accessibility state needs alt. Never manufacture a partial payload
    // from the identity — that points writers at an incompletely identified
    // editable.
    payload: payloadMatchesIdentity ? activePayload : null
  };

  if (identity.kind === SCOPE.PAGE) {
    const page = (state?.pages || []).find((p) => p?.id === state?.activePage);
    base.label = page?.name || 'Page';
    base.sublabel = '';
    return base;
  }

  if (identity.kind === SCOPE.MULTI) {
    base.label = `${identity.count} sections selected`;
    return base;
  }

  const section = identity.instanceId ? findSection(state, identity.instanceId) : null;
  const caps = identity.instanceId ? sectionCapabilities(identity.instanceId) : null;
  base.sharedType = caps?.sharedType ?? sharedTypeOf(state, identity.instanceId);
  base.locked = Boolean(caps?.locked);

  if (identity.kind === SCOPE.SECTION) {
    // The display-name ladder is shared/section-categories.js's, not a local
    // reimplementation; `formatSectionName` supplies the category map this
    // module never had, so the bar says "Hero" where the list says "Hero".
    base.label = formatSectionName(section);
    base.sublabel =
      section?.displayName && section.category
        ? formatSectionName({ category: section.category })
        : '';
    base.capabilities = caps;
    return base;
  }

  // text | image | element — the label names the ELEMENT, the sublabel names
  // the section it lives in, so the nested scope is legible without a second
  // glance. That pairing is the whole point of the two-colour hierarchy.
  const typeCaps = capabilitiesForTarget(base.payload || identity);
  base.label = kindLabel(identity.elementKind, { tagName: base.payload?.tagName });
  base.sublabel = section ? formatSectionName(section) : '';
  base.capabilities = typeCaps;
  base.hasAlt = identity.editableType === 'image' ? imageHasAlt(base.payload) : null;
  return base;
}

/**
 * The live scope. One instance per app; `start()` subscribes, `destroy()`
 * unsubscribes and cancels any pending frame.
 */
export class SelectionScope {
  constructor(opts = {}) {
    this._identity = null;
    this._viewModel = null;
    this._subscribers = new Set();
    this._offs = [];
    this._frame = null;
    this._epoch = null;
    this._started = false;
    // Injected so the class is testable without a browser and without this
    // module ever calling a global directly.
    //
    // BOUND TO `window`, not captured bare. `this._raf(cb)` invokes with
    // `this === the SelectionScope`, and a browser's requestAnimationFrame
    // throws "Illegal invocation" on a receiver that is not the window. jsdom
    // tolerates it, so the node suite passed while the real browser silently
    // did nothing — `SelectionStore._commit` catches subscriber errors and
    // console.errors them, so the scope simply never updated. Found by the
    // geometry gate; the node tests could not have found it.
    const win = typeof window !== 'undefined' ? window : null;
    this._raf =
      opts.requestAnimationFrame ||
      (win && typeof win.requestAnimationFrame === 'function'
        ? win.requestAnimationFrame.bind(win)
        : (cb) => setTimeout(cb, 0));
    this._cancelRaf =
      opts.cancelAnimationFrame ||
      (win && typeof win.cancelAnimationFrame === 'function'
        ? win.cancelAnimationFrame.bind(win)
        : clearTimeout);
  }

  /** Current identity, or null before start(). */
  getIdentity() {
    return this._identity;
  }

  /** Current view model, or null before start(). */
  getViewModel() {
    return this._viewModel;
  }

  /**
   * @param {(change: {identity: object, viewModel: object, identityChanged: boolean}) => void} fn
   * @returns {() => void} unsubscribe
   */
  /**
   * Subscribe, and REPLAY the current scope to a late subscriber.
   *
   * Contract S2 ("emits once on mount, not only on change") was a property of
   * `start()` alone, which means it only held for subscribers that existed
   * BEFORE it ran. `ContextZone` is constructed after `start()`, so it received
   * nothing until the user changed the selection — the bar booted with an empty
   * morph zone, which is the exact failure S2 is written against.
   *
   * Every test missed it: the jsdom ones drive a fake scope, and the browser
   * one changed the selection immediately after mounting. The phone gate caught
   * it only because it mounted and measured without touching anything.
   *
   * Replaying here makes S2 a property of the SCOPE rather than of the order two
   * constructors happen to run in.
   */
  subscribe(fn) {
    if (typeof fn !== 'function') return () => {};
    this._subscribers.add(fn);
    if (this._started && this._identity) {
      // `identityChanged: true` — for a subscriber that has never seen a scope,
      // this IS a change, and it is what makes the zone build its first pane.
      fn({ identity: this._identity, viewModel: this._viewModel, identityChanged: true });
    }
    return () => this._subscribers.delete(fn);
  }

  /**
   * Subscribe to the stores and emit once immediately.
   *
   * The initial emission is not a nicety: without it the bar boots with no
   * pane mounted and stays blank until the user clicks something.
   */
  start() {
    if (this._started) return () => {};
    this._started = true;
    this._epoch = store.getEpoch?.() ?? null;

    // Identity inputs — settled stores only.
    this._offs.push(selection.subscribe(() => this._schedule()));
    this._offs.push(onActiveEditableChange(() => this._schedule()));

    // View-model inputs. Targeted signals, never a blanket state-changed
    // subscription — see the header for why this is the sanctioned exception
    // and not a violation of the 16C2 rule.
    for (const event of VIEW_MODEL_SIGNALS) {
      this._offs.push(eventBus.on(event, () => this._schedule()));
    }

    this._recompute({ force: true });
    return () => this.destroy();
  }

  destroy() {
    for (const off of this._offs) if (typeof off === 'function') off();
    this._offs = [];
    if (this._frame != null) {
      this._cancelRaf(this._frame);
      this._frame = null;
    }
    this._subscribers.clear();
    this._identity = null;
    this._viewModel = null;
    this._started = false;
  }

  /**
   * Coalesce to one recompute per frame. A burst of element clicks, or a
   * scrub firing `theme:override-changed` at 60 Hz, must not thrash row 2.
   */
  _schedule() {
    if (this._frame != null) return;
    this._frame = this._raf(() => {
      this._frame = null;
      this._recompute();
    });
  }

  _recompute({ force = false } = {}) {
    const state = store.getState();

    // Staleness: a project switch or a load invalidates everything captured
    // before it. Re-baseline and emit rather than reporting the old project's
    // selection against the new project's state.
    const epoch = store.getEpoch?.() ?? null;
    const epochChanged = epoch !== this._epoch;
    if (epochChanged) this._epoch = epoch;

    const identity = deriveScopeIdentity(state);
    const identityChanged = !sameIdentity(identity, this._identity);
    if (!identityChanged && !force && !epochChanged) {
      // Identity is unchanged, so no morph — but the VALUES may have moved
      // (an undo, a peer edit, a responsive scope switch). Recompute and
      // notify; consumers that only care about the pane can check the flag.
      this._viewModel = derivePaneViewModel(identity, state);
      this._notify(false);
      return;
    }

    this._identity = identity;
    this._viewModel = derivePaneViewModel(identity, state);
    this._notify(true);
  }

  _notify(identityChanged) {
    const change = {
      identity: this._identity,
      viewModel: this._viewModel,
      identityChanged
    };
    for (const fn of [...this._subscribers]) {
      try {
        fn(change);
      } catch (err) {
        // One bad subscriber must not stop the others repainting.
        console.error('[selection-scope] subscriber threw', err);
      }
    }
  }
}

/**
 * Targeted view-model refresh signals. Each one can change a VALUE the pane
 * shows without changing WHICH pane is shown:
 *   theme:override-changed        a background/spacing/typography write landed
 *   history-changed               undo/redo moved the values under us
 *   collab:remote-applied         a peer's op landed
 *   collab:locks-changed          a peer took or released this section
 *   responsive-edit-scope:changed the write target moved between breakpoints
 *   viewport:changed              the active breakpoint moved
 *   editing:selection-state       bold/italic/link state under the caret
 *   iframe:style-snapshot         fresh computed values arrived from preview
 *   iframe:text-selected          the iframe's raw selection snapshot changed
 *   toolbar:rename-page           the resting page label changed
 *   writability-changed           a write blocker was added or released
 */
const VIEW_MODEL_SIGNALS = Object.freeze([
  'theme:override-changed',
  'history-changed',
  'collab:remote-applied',
  'collab:locks-changed',
  'responsive-edit-scope:changed',
  'viewport:changed',
  'editing:selection-state',
  'iframe:style-snapshot',
  'iframe:text-selected',
  'toolbar:rename-page',
  'writability-changed'
]);

export { VIEW_MODEL_SIGNALS };
